import { useSyncExternalStore } from 'react';

/**
 * transportClock — high-frequency playhead position store, decoupled from React context.
 *
 * The audio engine writes the current beat here every animation frame during playback.
 * Reading it never triggers React renders; components that need a live playhead either
 * subscribe directly (and mutate DOM/canvas transforms) or use the usePlayheadBeat hook,
 * which re-renders only the subscribing component — not the whole AudioContext tree.
 */
const listeners = new Set();
let currentBeat = 0;

export const transportClock = {
  getBeat() {
    return currentBeat;
  },
  setBeat(beat) {
    if (!Number.isFinite(beat)) return;
    if (beat === currentBeat) return;
    currentBeat = beat;
    listeners.forEach((listener) => {
      try {
        listener(currentBeat);
      } catch (_) {
        // A broken subscriber must not stall the transport.
      }
    });
  },
  subscribe(listener) {
    listeners.add(listener);
    return () => listeners.delete(listener);
  }
};

/**
 * Live playhead beat for components that render it (playhead line, time readout).
 * Re-renders only the calling component at animation-frame rate during playback.
 */
export function usePlayheadBeat() {
  return useSyncExternalStore(transportClock.subscribe, transportClock.getBeat);
}
